import { z } from 'zod';
import { MOTIVO_MINIMO } from './comunes.js';
import { ENFERMEDADES_CRONICAS, INFECCIONES_TRANSMISION_SEXUAL, METODOS_ANTICONCEPTIVOS, RESPUESTA_ANTECEDENTE, RESULTADOS_BIOPSIA, RESULTADOS_CITOLOGIA, RESULTADOS_VPH, VACUNAS, } from '../catalogos/clinico.js';
/**
 * Historia clínica gineco-oncologica.
 *
 * La estructura sigue el orden de la NOM-004: antecedentes heredofamiliares,
 * personales no patologicos, personales patologicos, gineco-obstetricos,
 * padecimiento actual, exploracion fisica, y diagnostico con plan.
 *
 * Todos los bloques son opcionales salvo el padecimiento actual: una historia
 * se llena en varias visitas, y forzar el llenado completo de golpe solo
 * consigue que se rellene con datos falsos.
 */
const texto = (maximo = 600) => z.string().trim().max(maximo).default('');
const opcionDe = (valores) => z.union([z.literal(''), z.enum(valores)]).default('');
const antecedente = z.object({
    respuesta: z.enum(RESPUESTA_ANTECEDENTE).default('No preguntado'),
    detalle: texto(300),
});
export const esquemaHeredofamiliares = z.object({
    cancerMama: antecedente.default({}),
    cancerCervicouterino: antecedente.default({}),
    cancerOtro: antecedente.default({}),
    diabetes: antecedente.default({}),
    hipertension: antecedente.default({}),
    otros: texto(),
});
export const esquemaNoPatologicos = z.object({
    tabaquismo: z
        .object({
        fuma: z.enum(RESPUESTA_ANTECEDENTE).default('No preguntado'),
        cigarrillosDia: texto(40),
        anios: texto(40),
        exFumadora: z.boolean().default(false),
    })
        .default({}),
    alcoholismo: z
        .object({
        consume: z.enum(RESPUESTA_ANTECEDENTE).default('No preguntado'),
        frecuencia: texto(80),
    })
        .default({}),
    toxicomanias: antecedente.default({}),
    actividadFisica: texto(120),
    alimentacion: texto(120),
    vacunas: z.array(z.enum(VACUNAS)).default([]),
    vacunaVphDosis: texto(40),
});
export const esquemaPatologicos = z.object({
    cronicas: z.array(z.enum(ENFERMEDADES_CRONICAS)).default([]),
    cronicasDetalle: texto(),
    alergias: antecedente.default({}),
    cirugias: antecedente.default({}),
    hospitalizaciones: antecedente.default({}),
    transfusiones: antecedente.default({}),
    medicamentosActuales: texto(),
    inmunosupresion: z.boolean().default(false),
});
export const esquemaGinecoObstetricos = z.object({
    menarca: z.union([z.literal(''), z.coerce.number().int().min(6).max(25)]).default(''),
    ritmoMenstrual: texto(60),
    fechaUltimaMenstruacion: z
        .union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)])
        .default(''),
    menopausia: z
        .object({
        presente: z.boolean().default(false),
        edad: z.union([z.literal(''), z.coerce.number().int().min(25).max(70)]).default(''),
        terapiaHormonal: z.boolean().default(false),
    })
        .default({}),
    gestas: z.coerce.number().int().min(0).max(30).default(0),
    partos: z.coerce.number().int().min(0).max(30).default(0),
    cesareas: z.coerce.number().int().min(0).max(30).default(0),
    abortos: z.coerce.number().int().min(0).max(30).default(0),
    inicioVidaSexual: z.union([z.literal(''), z.coerce.number().int().min(8).max(60)]).default(''),
    parejasSexuales: z.union([z.literal(''), z.coerce.number().int().min(0).max(999)]).default(''),
    metodoAnticonceptivo: opcionDe(METODOS_ANTICONCEPTIVOS),
    metodoAnticonceptivoTiempo: texto(60),
    infeccionesTransmisionSexual: z.array(z.enum(INFECCIONES_TRANSMISION_SEXUAL)).default([]),
    infeccionesDetalle: texto(300),
    histerectomia: z
        .object({
        realizada: z.boolean().default(false),
        anio: texto(10),
        motivo: texto(200),
    })
        .default({}),
});
/** Antecedentes de tamizaje: es lo que orienta la conducta en colposcopia. */
export const esquemaTamizaje = z.object({
    citologia: z
        .object({
        fecha: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).default(''),
        resultado: opcionDe(RESULTADOS_CITOLOGIA),
        observaciones: texto(300),
    })
        .default({}),
    vph: z
        .object({
        fecha: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).default(''),
        resultado: opcionDe(RESULTADOS_VPH),
        observaciones: texto(300),
    })
        .default({}),
    biopsiaPrevia: z
        .object({
        fecha: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/)]).default(''),
        resultado: opcionDe(RESULTADOS_BIOPSIA),
        observaciones: texto(300),
    })
        .default({}),
    tratamientosPrevios: texto(),
});
export const esquemaExploracion = z.object({
    peso: texto(12),
    talla: texto(12),
    tensionArterial: texto(20),
    frecuenciaCardiaca: texto(12),
    temperatura: texto(12),
    exploracionMamaria: texto(),
    exploracionAbdominal: texto(),
    exploracionGenital: texto(),
    especuloscopia: texto(),
    tactoVaginal: texto(),
    otrosHallazgos: texto(),
});
export const esquemaGuardarHistoria = z.object({
    padecimientoActual: z.string().trim().min(3, 'Describa el padecimiento actual').max(2000),
    motivoConsulta: texto(300),
    heredofamiliares: esquemaHeredofamiliares.default({}),
    noPatologicos: esquemaNoPatologicos.default({}),
    patologicos: esquemaPatologicos.default({}),
    ginecoObstetricos: esquemaGinecoObstetricos.default({}),
    tamizaje: esquemaTamizaje.default({}),
    exploracion: esquemaExploracion.default({}),
    impresionDiagnostica: texto(1000),
    cie10: texto(20),
    plan: texto(1000),
    pronostico: texto(300),
    /** Obligatorio al modificar una historia ya firmada. */
    // Vacio al crearla; al corregirla, el mismo minimo que en el resto de los
    // documentos (FIND-049: antes la API aceptaba un motivo de un caracter).
    motivoEdicion: z
        .string()
        .trim()
        .max(300)
        .refine((motivo) => motivo === '' || motivo.length >= MOTIVO_MINIMO, {
        message: `El motivo necesita al menos ${MOTIVO_MINIMO} caracteres`,
    })
        .default(''),
});
/**
 * Indice de masa corporal a partir de peso en kilos y talla en metros o
 * centimetros. Devuelve `null` si los datos no permiten calcularlo.
 */
export function calcularImc(peso, talla) {
    const kilos = Number(String(peso).replace(',', '.'));
    let metros = Number(String(talla).replace(',', '.'));
    if (!Number.isFinite(kilos) || !Number.isFinite(metros) || kilos <= 0 || metros <= 0) {
        return null;
    }
    // Se admite la talla en centimetros, que es como suele anotarse.
    if (metros > 3)
        metros = metros / 100;
    const imc = kilos / (metros * metros);
    return Number.isFinite(imc) ? Math.round(imc * 10) / 10 : null;
}
export function clasificacionImc(imc) {
    if (imc === null)
        return '';
    if (imc < 18.5)
        return 'Bajo peso';
    if (imc < 25)
        return 'Normal';
    if (imc < 30)
        return 'Sobrepeso';
    if (imc < 35)
        return 'Obesidad grado I';
    if (imc < 40)
        return 'Obesidad grado II';
    return 'Obesidad grado III';
}
//# sourceMappingURL=historia.js.map